import { forwardRef } from 'react'
import { createTheme, LinkProps } from '@mui/material'
import { grey } from '@mui/material/colors'
import { renderTimeViewClock } from '@mui/x-date-pickers'
import { AppLinkProps, AppLink } from './utils/url'
declare module '@mui/material/styles' {
  interface Palette {
    gradient: Palette['primary']
    gradientLight: Palette['primary']
    trailStatus: { dark: string; main: string; light: string }
    freezeThaw: { main: string }
    surface: { section: string }
  }

  interface PaletteOptions {
    gradient: PaletteOptions['primary']
    gradientLight: PaletteOptions['primary']
    trailStatus?: { dark: string; main: string; light: string }
    freezeThaw?: { main: string }
    surface?: { section: string }
  }

  interface BreakpointOverrides {
    zero: true // custom breakpoint
    xxxs: true // custom breakpoint
    xxs: true // custom breakpoint
    xs: true
    sm: true
    md: true
    lg: true
    xl: true
  }
}

declare module '@mui/material/styles' {
  interface TypographyOptions {
    letterSpacing?: number
  }

  interface TypographyVariants {
    error: React.CSSProperties
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    error?: React.CSSProperties
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    error: true
  }
}

// Updates MUI links and buttons to use react-router instead of standard anchor tags
const LinkBehavior = forwardRef<HTMLAnchorElement, Omit<AppLinkProps, 'to'> & { href: AppLinkProps['to'] }>(
  (props, ref) => {
    const { href, ...other } = props
    // Map href (MUI) -> to (react-router)
    return <AppLink ref={ref} to={href} {...other} />
  },
)

const LETTER_SPACING = '0.1rem'
// darkest color of the card (Paper) gradient
const PAPER_BASE_COLOR = 'rgb(32, 32, 32)'

let AppTheme = createTheme({
  // single corner radius shared by buttons, inputs, chips, papers, and containers.
  // In sx, use `borderRadius: 1` to match it.
  shape: { borderRadius: 8 },
  typography: {
    // one letter spacing for every variant (body, headings, captions, buttons, and the inputs, labels, tabs, and
    // tables built on them) instead of MUI's per-variant defaults. See DESIGN_SYSTEM.md.
    allVariants: {
      letterSpacing: LETTER_SPACING,
    },
  },
  components: {
    MuiCssBaseline: {
      // plain form elements don't inherit the page's letter spacing by default
      styleOverrides: `
        button, input, select, textarea {
          letter-spacing: inherit;
        }
      `,
    },
  },
  palette: {
    mode: 'dark',
    primary: { main: '#ff7900', contrastText: '#000' },
    secondary: { main: grey[900], contrastText: '#fff' },
    success: { main: '#8ec666', contrastText: '#000' },
    warning: { main: '#ffc05f', contrastText: '#000' },
    error: { main: '#ec6458', contrastText: '#000' },
    // Trail status colors, matched to the public app so the ported preview cards render identically.
    trailStatus: { dark: '#ec6458', main: '#ffc05f', light: '#8ec666' },
    freezeThaw: { main: '#75d9fe' },
    // background of a grouped section (detail cards, form sections): a subtle step up from the card behind it
    surface: { section: 'rgba(255, 255, 255, 0.05)' },
    text: {
      primary: '#fff',
      secondary: '#fff',
    },
    gradient: {
      main: 'linear-gradient(340deg, rgba(0, 0, 0, 1) 0%, rgba(41, 41, 41, 1) 73%, #4a4a4c 100%)',
      contrastText: '#fff',
    },
    gradientLight: {
      main: `linear-gradient(340deg, ${PAPER_BASE_COLOR} 0%, rgba(41, 41, 41, 1) 73%, #4a4a4c 100%)`,
      contrastText: '#fff',
    },
  },
  breakpoints: {
    values: {
      zero: 0,
      xxxs: 300,
      xxs: 390,
      xs: 425,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
})

// this section is for theme props that rely on palette or breakpoints defined above dynamically
AppTheme = createTheme(AppTheme, {
  typography: {
    error: {
      color: AppTheme.palette.error.main,
      fontWeight: 700,
    },
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
        LinkComponent: LinkBehavior,
      },
    },
    MuiLink: {
      defaultProps: {
        component: LinkBehavior,
      } as LinkProps,
      styleOverrides: {
        root: {
          textDecoration: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          // gradientLight run from the light top-left corner, with its stops capped at the distances they fall at on a
          // list page's card. A percentage-only gradient stretches with the card, so a tall card (a detail page) would get
          // a far bigger light corner than a short one; with the caps every card shades the same, and past the last stop
          // the color simply stays at the darkest shade, so there's no visible edge. Short cards (menus, dialogs) still
          // use the percentages and shade as before.
          background: `linear-gradient(160deg, #4a4a4c 0%, rgba(41, 41, 41, 1) min(27%, 280px), ${PAPER_BASE_COLOR} min(100%, 1030px))`,
          color: AppTheme.palette.gradientLight.contrastText,
          '& .MuiDateTimePickerToolbar-ampmLabel:not(.Mui-selected)': {
            // fix the styling of the datetime picker am/pm selector being all white no matter what is selected
            color: AppTheme.palette.grey[600],
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          background: AppTheme.palette.secondary.main,
          '&.Mui-focused': {
            color: AppTheme.palette.secondary.contrastText,
          },
        },
        shrink: {
          borderRadius: AppTheme.shape.borderRadius,
          padding: '2px 8px',
          marginLeft: '-4px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          // chips default to a pill shape; match the rounding of buttons and containers instead
          borderRadius: AppTheme.shape.borderRadius,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        // dark field background on the (rounded) root rather than the inner input element, whose square corners
        // would otherwise show outside the rounded outline; this also covers start/end adornments
        root: {
          background: AppTheme.palette.secondary.main,
        },
      },
    },
    MuiDesktopTimePicker: {
      defaultProps: {
        viewRenderers: {
          hours: renderTimeViewClock,
          minutes: renderTimeViewClock,
          seconds: renderTimeViewClock,
        },
      },
    },
    MuiDesktopDateTimePicker: {
      defaultProps: {
        viewRenderers: {
          hours: renderTimeViewClock,
          minutes: renderTimeViewClock,
          seconds: renderTimeViewClock,
        },
      },
    },
  },
})

export default AppTheme
